import React from 'react';
import ReactTooltip from 'react-tooltip';
import hash from 'object-hash';
import styled from 'styled-components';
import { Product } from '@/features/editor/widgets/custom-widget/loading-section/shared/models';
import { HSpace } from '@/components/spacing';
import { InventoryQty } from '@/features/editor/widgets/custom-widget/loading-section/shared/inventory-qty';
import { currencySymbol } from '@/utils/currencies';
import { SlotActions } from '@/features/editor/widgets/custom-widget/loading-section/shared/slot-actions';

type Props = {
  onClick?: () => void;
  p: Product;
  slotNum?: number;
  onDelete?: () => void;
  onMoreFilters?: () => void;
  onImageClick?: () => void;
  filterLen?: number;
  draggableAttributes?: any;
  draggableListeners?: any;
  index?: number;
  showHandleTooltip?: boolean;
};

export function ProductTile({
  onClick,
  slotNum,
  p: {
    compareAtPrice,
    handle,
    image,
    images,
    mainValue,
    price,
    title,
    inventoryQuantity,
    id,
  },
  onMoreFilters,
  filterLen,
  onDelete,
  onImageClick,
  draggableAttributes,
  draggableListeners,
  index,
  showHandleTooltip,
}: Props) {
  const isClickable = typeof onClick !== `undefined`;
  const symbol = currencySymbol();
  const hasMultipleImages = images && images.length > 1;
  const canChangeImage = !!onImageClick && (hasMultipleImages || !images);
  const showTooltip = showHandleTooltip && !!handle;
  const tooltipId = showTooltip
    ? `product-tile-tooltip-${hash({ id, mainValue })}`
    : ``;

  const handleImageClick = (e: React.MouseEvent) => {
    if (onImageClick && canChangeImage) {
      e.stopPropagation();
      onImageClick();
    }
  };

  return (
    <SearchResultItem onClick={onClick} isClickable={isClickable}>
      <ProductWrapper>
        <ImageBox onClick={handleImageClick} isClickable={canChangeImage}>
          <img src={image?.src} alt={handle} />
          {canChangeImage && (
            <ImageOverlay>
              <OverlayText>Change Image</OverlayText>
            </ImageOverlay>
          )}
        </ImageBox>
        <HSpace value={2} />
        <ProductDetails>
          {showTooltip && (
            <ReactTooltip
              id={tooltipId}
              place="bottom"
              delayHide={0}
              textColor="#fff"
              className="product-tile-tooltip"
            >
              {handle}
            </ReactTooltip>
          )}
          <ProductTitleWrapper
            {...(showTooltip && { 'data-tip': true, 'data-for': tooltipId })}
          >
            <ProductTitle>{title}</ProductTitle>
            <InventoryQty inventoryQuantity={inventoryQuantity} />
          </ProductTitleWrapper>
          {mainValue && <Color>Color: {mainValue}</Color>}
          <Price>
            <span>
              {symbol}
              {price || 0}
            </span>
            {compareAtPrice > 0 && compareAtPrice !== price && (
              <span>
                {symbol}
                {compareAtPrice}
              </span>
            )}
          </Price>
        </ProductDetails>
        {slotNum >= 0 && <SlotNum>{index + 1}</SlotNum>}
      </ProductWrapper>
      {slotNum >= 0 && (
        <SlotActions
          slotNum={slotNum}
          onDelete={onDelete}
          onMoreFilters={onMoreFilters}
          filterLen={filterLen}
          onClick={onClick}
          draggableAttributes={draggableAttributes}
          draggableListeners={draggableListeners}
        />
      )}
    </SearchResultItem>
  );
}

const Price = styled.div`
  margin-top: 1rem;
  display: flex;
  line-height: 1.71;
  font-family: 'Inter', serif;
  font-size: 1.25rem;

  span:first-child {
    margin-right: 1rem;
    color: #000;
  }
  span:nth-child(2) {
    color: #798794;
    text-decoration: line-through;
  }
`;
const Color = styled.span`
  margin-top: 1rem;
  line-height: 1.71;
  font-family: 'Inter', serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: #000;
  text-transform: capitalize;
`;
const ProductTitleWrapper = styled.span`
  display: flex;
  flex-wrap: wrap;
  column-gap: 1rem;
  align-items: baseline;
  text-transform: capitalize;
  font-family: 'Inter', serif;
  font-size: 1.35rem;
`;
const ProductTitle = styled.span`
  text-transform: capitalize;
  font-family: 'Inter', serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #000;
  line-height: 1.71;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  white-space: pre-wrap;
`;
const ImageBox = styled.div`
  position: relative;
  width: 35%;
  min-width: 35%;
  aspect-ratio: 1;
  height: auto;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: ${(p: { isClickable?: boolean }) =>
    p.isClickable ? `pointer` : `default`};

  img {
    border-radius: 4%;
    max-height: 100%;
    max-width: 100%;
    box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.1);
    border: solid 1px rgba(151, 151, 151, 0.11);
    transition: opacity 0.2s ease;
  }

  ${(p: { isClickable?: boolean }) =>
    p.isClickable
      ? `
    &:hover img {
      opacity: 0.7;
    }
    &:hover > div {
      opacity: 1;
    }
  `
      : ``}
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4%;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const OverlayText = styled.span`
  color: white;
  font-family: 'Inter', serif;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  padding: 0.5rem 1rem;
`;
const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  && .product-tile-tooltip {
    border-radius: 1rem !important;
    font-family: 'JetBrains Mono', serif;
    opacity: 1 !important;
    background: #000000bb !important;
    max-width: 30rem;
  }
`;
const ProductWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-right: 20px;
  width: 100%;
  height: 100%;
`;
export const SlotNum = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 30px;
  height: 30px;
  font-size: 1.4rem;
  text-align: center;
  line-height: 30px;
  border-radius: 100%;
  color: #595959;
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0),
    0 10px 12px 0 rgba(151, 210, 206, 0.32), 0 2px 2px 0 rgba(0, 0, 0, 0.25);
  background-color: #fff;
`;
export const SearchResultItem = styled.div`
  position: relative;
  ${(p: any) =>
    p.isClickable
      ? `
    &:hover {
    cursor: pointer;
    background-color: #ebebeb;
  }
  &:active {
    cursor: default;
    background-color: #f5f5f5;
    box-shadow: none;
  }
  `
      : ``}

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4% 4% 2.5%;
  border-radius: 10px;
  min-width: 33rem;
  height: 100%;
  justify-content: space-between;
  background-color: #f5f5f5;
`;
